import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const giorniAllaScadenza = (value) => {
  if (!value) return null;

  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);

  const data = new Date(`${value}T00:00:00`);
  data.setHours(0, 0, 0, 0);

  if (Number.isNaN(data.getTime())) return null;

  return Math.ceil(
    (data.getTime() - oggi.getTime()) / 86400000
  );
};

const formatoData = (value) =>
  value ? value.split("-").reverse().join("/") : "-";

const statoScadenza = (giorni) => {
  if (giorni < 0) {
    return {
      label: "Scaduto",
      card: "border-red-200 bg-red-50/70",
      title: "text-red-700",
      badge: "border-red-200 bg-red-50 text-red-700",
      icon: "bg-red-100 text-red-600",
    };
  }

  return {
    label:
      giorni === 0
        ? "Scade oggi"
        : giorni === 1
          ? "Scade domani"
          : `Scade tra ${giorni} giorni`,
    card: "border-orange-200 bg-orange-50/60",
    title: "text-orange-700",
    badge: "border-orange-200 bg-orange-50 text-orange-700",
    icon: "bg-orange-100 text-orange-600",
  };
};

export default function ScadenzeDocumentiCard() {
  const navigate = useNavigate();
  const [avvisi, setAvvisi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagamentoInCorso, setPagamentoInCorso] = useState(null);

  useEffect(() => {
    let active = true;

    const carica = async () => {
      try {
        const [operaiResponse, attrezzatureResponse, scadenziarioResponse] =
          await Promise.all([
            api.get("/gestionale/operai", {
              params: { include_archived: false },
            }),
            api.get("/attrezzi-catalogo"),
            api.get("/gestionale/scadenziario"),
          ]);

        const operai = Array.isArray(operaiResponse.data)
          ? operaiResponse.data
          : [];

        const dettagliOperai = await Promise.all(
          operai.map(async (operaio) => {
            try {
              return (
                await api.get(
                  `/gestionale/operai/${operaio.id}`
                )
              ).data;
            } catch {
              return null;
            }
          })
        );

        const elenco = [];

        dettagliOperai
          .filter(Boolean)
          .forEach((operaio) => {
            const documenti = Array.isArray(operaio.documenti)
              ? operaio.documenti
              : [];

            documenti
              .filter(
                (documento) =>
                  !documento.archived_at &&
                  documento.scadenza
              )
              .forEach((documento) => {
                const giorni = giorniAllaScadenza(
                  documento.scadenza
                );

                if (giorni !== null && giorni <= 30) {
                  elenco.push({
                    id: `operaio-${operaio.id}-${documento.id}`,
                    giorni,
                    scadenza: documento.scadenza,
                    categoria: "Documento operaio",
                    nome:
                      `${operaio.cognome || ""} ${
                        operaio.nome || ""
                      }`.trim() || "Operaio",
                    dettaglio:
                      documento.tipo ||
                      documento.nome ||
                      "Documento personale",
                    route:
                      `/gestionale/operai/${operaio.id}` +
                      `?documento=${documento.id}`,
                  });
                }
              });
          });

        const attrezzature = Array.isArray(
          attrezzatureResponse.data
        )
          ? attrezzatureResponse.data
          : [];

        attrezzature.forEach((attrezzatura) => {
          [
            ["bollo", "Bollo"],
            ["assicurazione", "Assicurazione"],
            ["revisione", "Revisione"],
            ["libretto_omologazione", "Libretto / Omologazione"],
          ].forEach(([campo, etichetta]) => {
            const scadenza =
              attrezzatura[`${campo}_scadenza`];

            if (!attrezzatura[campo] || !scadenza) return;

            const giorni = giorniAllaScadenza(scadenza);

            if (giorni !== null && giorni <= 30) {
              elenco.push({
                id: `attrezzatura-${attrezzatura.id}-${campo}`,
                giorni,
                scadenza,
                categoria: "Documento attrezzatura",
                nome: attrezzatura.nome || "Attrezzatura",
                dettaglio: etichetta,
                route:
                  "/gestionale/attrezzature" +
                  `?attrezzatura=${attrezzatura.id}`,
              });
            }
          });
        });

        const scadenze = Array.isArray(scadenziarioResponse.data?.scadenze)
          ? scadenziarioResponse.data.scadenze
          : [];

        scadenze.forEach((scadenza) => {
          if (scadenza.pagata || !scadenza.data_scadenza) return;
          const giorni = giorniAllaScadenza(scadenza.data_scadenza);
          const residuo = Number(scadenza.residuo ?? scadenza.importo ?? 0);
          if (giorni === null || giorni > 5 || residuo <= 0) return;
          const fattura = scadenza.numeri_fatture || scadenza.numero_fattura || "";
          elenco.push({
            id: `scadenziario-${scadenza.id}`,
            scadenzaId: scadenza.id,
            giorni,
            scadenza: scadenza.data_scadenza,
            categoria: "Scadenza fornitore",
            nome: scadenza.fornitore || "Fornitore",
            dettaglio: `${fattura ? `Fattura ${fattura} - ` : ""}${residuo.toLocaleString("it-IT", { style: "currency", currency: "EUR" })}`,
            route: "/gestionale/scadenziario",
            pagabile: true,
            tipoPagamento: scadenza.tipo_pagamento || "bonifico",
            riferimento: fattura,
          });
        });

        elenco.sort((a, b) => a.giorni - b.giorni);

        if (active) setAvvisi(elenco);
      } catch {
        if (active) setAvvisi([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    carica();

    return () => {
      active = false;
    };
  }, []);

  const segnaPagata = async (event, avviso) => {
    event.stopPropagation();
    const adesso = new Date();
    const dataPagamento = `${adesso.getFullYear()}-${String(adesso.getMonth() + 1).padStart(2, "0")}-${String(adesso.getDate()).padStart(2, "0")}`;
    setPagamentoInCorso(avviso.id);
    try {
      await api.post(`/gestionale/scadenziario/scadenze/${avviso.scadenzaId}/paga`, {
        data_pagamento: dataPagamento,
        modalita: avviso.tipoPagamento,
        riferimento: avviso.riferimento,
        note: "Pagamento registrato dalla dashboard",
      });
      setAvvisi((correnti) => correnti.filter((item) => item.id !== avviso.id));
      toast.success("Scadenza segnata come pagata");
    } catch (errore) {
      toast.error(errore.response?.data?.detail || "Impossibile registrare il pagamento");
    } finally {
      setPagamentoInCorso(null);
    }
  };

  if (loading || avvisi.length === 0) return null;

  return (
    <>
      {avvisi.map((avviso) => {
        const stato = statoScadenza(avviso.giorni);

        return (
          <section
            key={avviso.id}
            className={
              "min-h-[118px] rounded-xl border p-6 " +
              stato.card
            }
          >
            <div className="h-full w-full">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p
                  className={
                    "text-xs font-bold uppercase tracking-[0.15em] " +
                    stato.title
                  }
                >
                  {avviso.categoria}
                </p>

                <div
                  className={
                    "rounded-md p-1.5 " + stato.icon
                  }
                >
                  <CalendarClock className="h-4 w-4" />
                </div>
              </div>

              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(avviso.route)}
                    className={
                      "block max-w-full truncate text-left font-heading text-lg font-bold " +
                      stato.title
                    }
                  >
                    {avviso.nome}
                  </button>

                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {avviso.dettaglio}
                  </p>
                </div>

                <div className="shrink-0 text-right">
                  <span
                    className={
                      "inline-flex rounded-full border px-2 py-0.5 " +
                      "text-[11px] font-semibold " +
                      stato.badge
                    }
                  >
                    {stato.label}
                  </span>

                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {formatoData(avviso.scadenza)}
                  </p>
                  {avviso.pagabile && (
                    <button
                      type="button"
                      disabled={pagamentoInCorso === avviso.id}
                      onClick={(event) => segnaPagata(event, avviso)}
                      className="mt-2 inline-flex items-center gap-1 rounded-md bg-orange-600 px-2 py-1 text-[11px] font-bold text-white shadow-sm hover:bg-orange-700 disabled:opacity-60"
                    >
                      <Check className="h-3 w-3" />
                      {pagamentoInCorso === avviso.id ? "Salvataggio..." : "Pagato"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}
